/**
 * VisorPdf — ver un PDF dentro de Clobi antes de bajarlo.
 *
 * Lo pidió RR. HH.: en Nómina y en el portal todos los PDF solo se podían
 * descargar. Ahora «Ver» abre el visor NATIVO del navegador dentro de un modal,
 * con su barra de siempre (páginas, zoom, imprimir, descargar).
 *
 * ⚠️ Además hay un «Descargar» propio en el pie, y no sobra: desde la barra del
 * visor nativo el archivo de un blob se baja con un nombre aleatorio
 * (`3f9a….pdf`), no con `liquidacion_11111111_1_2026_09.pdf`.
 *
 * 🔴 En pantallas chicas NO se usa el modal: Chrome en Android no muestra un PDF
 * dentro de un iframe (queda en blanco) y Safari en iPhone muestra mal los de
 * varias hojas. Ahí «Ver» abre el visor del teléfono en una pestaña nueva. La
 * pestaña se abre EN EL MISMO CLIC y se llena cuando llega el PDF: abierta
 * después de un `await`, el navegador la trata como ventana emergente y la
 * bloquea.
 *
 * Uso:
 *   const { ver, visor } = useVisorPdf()
 *   <button onClick={() => ver({ titulo, nombreArchivo, cargar })}>Ver</button>
 *   {visor}
 */
import { useEffect, useRef, useState } from 'react'
import type { AxiosInstance } from 'axios'
import { Download, ExternalLink, Loader2 } from 'lucide-react'
import { Modal } from './ds/Modal'
import { Button } from './ds/Button'
import { useToast } from './Toast'

/** El PDF: el binario mismo, o una URL firmada que el navegador puede mostrar. */
export type ContenidoPdf = Blob | string

export interface PdfAVer {
  /** Encabezado del visor: «Liquidación 2026-09 · Ana Prueba». */
  titulo: string
  /** Con qué nombre se baja desde el botón «Descargar». */
  nombreArchivo: string
  /** Trae el PDF para mostrarlo. Si es una URL firmada, debe ser `inline`. */
  cargar: () => Promise<ContenidoPdf>
  /**
   * Solo para URLs firmadas: la URL de descarga (`attachment`, con el nombre).
   * Sin esto se baja lo mismo que se ve.
   */
  urlDeDescarga?: () => Promise<string>
}

/** Baja un archivo con su nombre. Un enlace con `download` no lo bloquea el
 *  navegador aunque venga después de un `await`; un `window.open` sí. */
export function bajarArchivo(href: string, nombre: string) {
  const a = document.createElement('a')
  a.href = href
  a.download = nombre
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

/** Sin `type`, el navegador baja el blob en vez de mostrarlo: en desarrollo el
 *  backend sirve el binario y llega sin tipo o con uno genérico. */
function comoPdf(blob: Blob): Blob {
  return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })
}

/**
 * Un documento de la carpeta digital (`…/download/`), que responde `{url}` con
 * la URL firmada de S3, o el archivo mismo en desarrollo sin S3.
 */
export function documentoGuardado(
  client: AxiosInstance, ruta: string,
): Pick<PdfAVer, 'cargar' | 'urlDeDescarga'> {
  const pedir = async (inline: boolean): Promise<ContenidoPdf> => {
    const res = await client.get(ruta, { responseType: 'blob', params: inline ? { inline: 1 } : {} })
    if (res.data.type === 'application/json') {
      const { url } = JSON.parse(await res.data.text())
      if (!url) throw new Error('Sin enlace')
      return url as string
    }
    return res.data as Blob
  }
  return {
    cargar: () => pedir(true),
    urlDeDescarga: async () => {
      const r = await pedir(false)
      // Sirve también para lo que no es PDF: el blob se baja con su tipo.
      return typeof r === 'string' ? r : URL.createObjectURL(r)
    },
  }
}

/**
 * `cargar` se llama UNA vez por PDF abierto, aunque el efecto corra dos veces
 * (React StrictMode en desarrollo). No es solo ahorro: el certificado emite
 * dentro de `cargar`, y dos llamadas simultáneas creaban dos certificados del
 * mismo día.
 */
const enCurso = new WeakMap<PdfAVer, Promise<ContenidoPdf>>()
function cargarUnaVez(pdf: PdfAVer) {
  let promesa = enCurso.get(pdf)
  if (!promesa) {
    promesa = pdf.cargar()
    enCurso.set(pdf, promesa)
  }
  return promesa
}

/** ≤767 px: celulares. Las tabletas sí muestran el PDF dentro de la página. */
function esPantallaChica() {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
}

export function useVisorPdf() {
  const [actual, setActual] = useState<PdfAVer | null>(null)
  const toast = useToast()

  const ver = (pdf: PdfAVer) => {
    if (!esPantallaChica()) {
      setActual(pdf)
      return
    }
    // Sin `noopener`: con él `window.open` devuelve null y no hay a qué
    // ponerle la dirección. ⚠️ Y tampoco se corta el `opener` después: Chrome
    // no deja navegar a un `blob:` desde una pestaña sin vínculo con quien lo
    // creó (queda en about:blank). Lo que se abre es el PDF de Clobi o una URL
    // firmada de S3, que no ejecutan código, así que el vínculo no expone nada.
    const pestana = window.open('', '_blank')
    if (!pestana) {
      setActual(pdf) // Bloqueada igual: el modal es mejor que nada.
      return
    }
    pestana.document.title = pdf.titulo
    pdf.cargar().then(
      (contenido) => {
        if (typeof contenido === 'string') {
          pestana.location.href = contenido
        } else {
          const url = URL.createObjectURL(comoPdf(contenido))
          pestana.location.href = url
          // La pestaña ya lo leyó; sin esto queda en memoria hasta cerrar Clobi.
          setTimeout(() => URL.revokeObjectURL(url), 60_000)
        }
      },
      () => {
        pestana.close()
        toast.error('No se pudo abrir el documento.')
      },
    )
  }

  const visor = actual ? <VisorPdf pdf={actual} onClose={() => setActual(null)} /> : null
  return { ver, visor }
}

function VisorPdf({ pdf, onClose }: { pdf: PdfAVer; onClose: () => void }) {
  const toast = useToast()
  const [url, setUrl] = useState('')
  const [error, setError] = useState(false)
  const [bajando, setBajando] = useState(false)
  const blob = useRef<Blob | null>(null)

  useEffect(() => {
    let vivo = true
    let creada = ''
    cargarUnaVez(pdf).then(
      (contenido) => {
        if (!vivo) return
        if (typeof contenido === 'string') {
          setUrl(contenido)
        } else {
          blob.current = comoPdf(contenido)
          creada = URL.createObjectURL(blob.current)
          setUrl(creada)
        }
      },
      () => { if (vivo) setError(true) },
    )
    return () => {
      vivo = false
      if (creada) URL.revokeObjectURL(creada)
    }
  }, [pdf])

  const descargar = async () => {
    setBajando(true)
    try {
      if (blob.current) {
        const local = URL.createObjectURL(blob.current)
        bajarArchivo(local, pdf.nombreArchivo)
        setTimeout(() => URL.revokeObjectURL(local), 60_000)
      } else {
        bajarArchivo(pdf.urlDeDescarga ? await pdf.urlDeDescarga() : url, pdf.nombreArchivo)
      }
    } catch {
      toast.error('No se pudo descargar el documento.')
    } finally {
      setBajando(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={pdf.titulo}
      size="2xl"
      fill
      sheetOnMobile={false}
      actions={
        <>
          {url && (
            // La salida si el navegador no muestra el PDF dentro de la página.
            <a href={url} target="_blank" rel="noopener noreferrer"
              className="mr-auto inline-flex items-center gap-1 text-xs text-content-muted hover:text-content underline">
              <ExternalLink size={12} aria-hidden="true" /> Abrir en una pestaña nueva
            </a>
          )}
          <Button variant="secondary" onClick={onClose}>Cerrar</Button>
          <Button variant="primary" onClick={descargar} loading={bajando} disabled={!url}
            icon={<Download size={14} aria-hidden="true" />}>
            Descargar
          </Button>
        </>
      }
    >
      <div className="flex-1 min-h-0 h-[75vh] bg-surface-muted">
        {error ? (
          <div className="h-full flex items-center justify-center text-sm text-content-muted px-6 text-center">
            No se pudo cargar el documento. Cerrar e intentar de nuevo.
          </div>
        ) : url ? (
          <iframe src={url} title={pdf.titulo} className="w-full h-full bg-white" />
        ) : (
          <div className="h-full flex items-center justify-center text-content-muted" role="status">
            <Loader2 size={20} className="animate-spin" aria-hidden="true" />
            <span className="sr-only">Cargando el documento</span>
          </div>
        )}
      </div>
    </Modal>
  )
}
